import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Upload, Loader2, Trash2, CheckSquare, Square, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { createClient, createSparePart } from '../../../redux/api';
import { isFeatureAllowed, getPackageRestrictionMessage } from '../../../utils/packageLimits';
import { mergeAuthProfile } from '../../../utils/mergeAuthProfile';
import { API_BASE_URL } from '../../../config/api';
import {
  normalizePhoneForSave,
  isLikelyWrongAiPhone,
  isPhoneStructurallyValid,
  aiPhonesMismatch
} from '../../../utils/phoneValidation';
import PhoneCropPreview from '../../common/PhoneCropPreview';

const MAX_FILES = 4;
const safeText = (v) => (typeof v === 'string' ? v.trim() : '');

const isImageFile = (file) => {
  if (!file) return false;
  const name = file.name || '';
  const type = file.type || '';
  if (type.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|bmp|tiff?|svg)$/i.test(name);
};

const normalizeExtractedClient = (c) => {
  const addressObj = c?.address && typeof c.address === 'object' ? c.address : {};
  const primary = safeText(c?.phoneFromPrimaryAI || c?.phoneRaw || c?.phone);
  const acceptedPhone = c?.phoneNeedsReview ? '' : safeText(c?.phone);
  const normalizedPhone = acceptedPhone
    ? normalizePhoneForSave(acceptedPhone)
    : primary
      ? normalizePhoneForSave(primary)
      : '';

  return {
    selected: !c?.phoneNeedsReview,
    name: safeText(c?.name),
    phone: normalizedPhone,
    phoneRaw: safeText(c?.phoneRaw || primary),
    phoneFromPrimaryAI: primary,
    phoneFromVerifierAI: safeText(c?.phoneFromVerifierAI),
    phoneOcrReading: safeText(c?.phoneOcrReading),
    phoneCropBox: c?.phoneCropBox || null,
    phoneNeedsReview: Boolean(c?.phoneNeedsReview),
    phoneAutoAccepted: Boolean(c?.phoneAutoAccepted),
    email: safeText(c?.email),
    // Napomenu ostavlja korisnik da popuni ručno
    description: '',
    rating: null,
    address: {
      street: safeText(addressObj?.street),
      number: safeText(addressObj?.number),
      floor: safeText(addressObj?.floor),
      apartment: safeText(addressObj?.apartment),
      isHouse: Boolean(addressObj?.isHouse),
      countryCode: safeText(addressObj?.countryCode)
    }
  };
};

const normalizeExtractedSparePart = (p) => {
  const safeText = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    return '';
  };

  const safeNumberString = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v === 'string') {
      const t = v.trim();
      if (!t) return '';
      const n = parseFloat(t.replace(',', '.'));
      return Number.isFinite(n) ? String(n) : '';
    }
    return '';
  };

  return {
    selected: true,
    name: safeText(p?.name),
    code: safeText(p?.code),
    quantity: safeNumberString(p?.quantity),
    unitPrice: safeNumberString(p?.unitPrice),
    description: ''
  };
};

const AIImportView = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const authForPackage = useMemo(() => mergeAuthProfile(user), [user]);
  const canUseAI = isFeatureAllowed('ai', authForPackage);
  const restrictionMsg = getPackageRestrictionMessage('ai', authForPackage);

  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [instructions, setInstructions] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [extractedClients, setExtractedClients] = useState([]);
  const [saveClientErrors, setSaveClientErrors] = useState([]);
  
  // Spare parts extraction
  const [spareFiles, setSpareFiles] = useState([]);
  const [sparePreviews, setSparePreviews] = useState([]);
  const [isExtractingSpare, setIsExtractingSpare] = useState(false);
  const [isSavingSpare, setIsSavingSpare] = useState(false);
  const [extractedSpareParts, setExtractedSpareParts] = useState([]);

  const selectedCount = useMemo(
    () => extractedClients.filter((c) => c.selected).length,
    [extractedClients]
  );

  const spareSelectedCount = useMemo(
    () => extractedSpareParts.filter((p) => p.selected).length,
    [extractedSpareParts]
  );

  const onPickFiles = async (picked) => {
    const arr = Array.from(picked || []);
    if (arr.length === 0) return;

    const next = [...files, ...arr].slice(0, MAX_FILES);
    setFiles(next);

    const nextPreviews = await Promise.all(
      next.map(
        (f) =>
          new Promise((resolve) => {
            if (!isImageFile(f)) {
              resolve(null);
              return;
            }
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(f);
          })
      )
    );
    setPreviews(nextPreviews);
  };

  const removeFileAt = (idx) => {
    const nextFiles = files.filter((_, i) => i !== idx);
    const nextPrev = previews.filter((_, i) => i !== idx);
    setFiles(nextFiles);
    setPreviews(nextPrev);
  };

  const toggleAll = (checked) => {
    setExtractedClients((prev) => prev.map((c) => ({ ...c, selected: checked })));
  };

  const updateClientField = (idx, field, value) => {
    setExtractedClients((prev) => {
      const next = [...prev];
      const row = { ...next[idx], [field]: value };
      if (field === 'phone') {
        row.phoneNeedsReview = false;
        row.selected = true;
      }
      next[idx] = row;
      return next;
    });
  };

  const updateClientAddressField = (idx, field, value) => {
    setExtractedClients((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], address: { ...next[idx].address, [field]: value } };
      return next;
    });
  };

  const toggleClientSelected = (idx) => {
    setExtractedClients((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], selected: !next[idx].selected };
      return next;
    });
  };

  const onPickSpareFiles = async (picked) => {
    const arr = Array.from(picked || []);
    if (arr.length === 0) return;

    const next = [...spareFiles, ...arr].slice(0, MAX_FILES);
    setSpareFiles(next);

    const nextPreviews = await Promise.all(
      next.map(
        (f) =>
          new Promise((resolve) => {
            if (!isImageFile(f)) {
              resolve(null);
              return;
            }
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(f);
          })
      )
    );
    setSparePreviews(nextPreviews);
  };

  const removeSpareFileAt = (idx) => {
    const nextFiles = spareFiles.filter((_, i) => i !== idx);
    const nextPrev = sparePreviews.filter((_, i) => i !== idx);
    setSpareFiles(nextFiles);
    setSparePreviews(nextPrev);
  };

  const toggleSpareAll = (checked) => {
    setExtractedSpareParts((prev) => prev.map((p) => ({ ...p, selected: checked })));
  };

  const updateSpareField = (idx, field, value) => {
    setExtractedSpareParts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const toggleSparePartSelected = (idx) => {
    setExtractedSpareParts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], selected: !next[idx].selected };
      return next;
    });
  };

  const extractSparePartsFromImages = async () => {
    if (!canUseAI) {
      toast.error(restrictionMsg || 'AI Import nije dostupan u ovom paketu');
      return;
    }
    if (spareFiles.length === 0) {
      toast.error('Dodaj bar jednu sliku.');
      return;
    }

    setIsExtractingSpare(true);
    try {
      const fd = new FormData();
      spareFiles.forEach((f) => fd.append('images', f));

      const res = await fetch(`${API_BASE_URL}/api/ai-import/spare-parts-from-images`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user?.token}`
        },
        body: fd
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || 'Greška pri AI ekstrakciji');
      }

      const normalized = Array.isArray(data?.spareParts)
        ? data.spareParts.map(normalizeExtractedSparePart)
        : [];

      setExtractedSpareParts(normalized);
      toast.success(`Ekstrahovano rezervnih delova: ${normalized.length}`);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Greška pri AI ekstrakciji');
    } finally {
      setIsExtractingSpare(false);
    }
  };

  const saveSelectedSpareParts = async () => {
    if (spareSelectedCount === 0) {
      toast.error('Nema čekiranih rezervnih delova za čuvanje.');
      return;
    }

    setIsSavingSpare(true);
    try {
      const selected = extractedSpareParts.filter((p) => p.selected);
      let ok = 0;
      let fail = 0;

      for (const p of selected) {
        try {
          const payload = {
            name: p.name,
          };

          if (p.code !== '') payload.code = p.code;
          if (p.quantity !== '') payload.quantity = parseInt(p.quantity, 10);
          if (p.unitPrice !== '') payload.price = parseFloat(p.unitPrice);

          await createSparePart(payload);
          ok++;
        } catch (err) {
          fail++;
          console.warn('Create spare part failed:', err?.response?.data || err?.message);
        }
      }

      if (ok > 0) toast.success(`Sačuvano rezervnih delova: ${ok}`);
      if (fail > 0) toast.warn(`Nije sačuvano: ${fail} (proveri duplikate / podatke / limit paketa)`);
    } finally {
      setIsSavingSpare(false);
    }
  };

  const extractFromImages = async () => {
    if (!canUseAI) {
      toast.error(restrictionMsg || 'AI Import nije dostupan u ovom paketu');
      return;
    }
    if (files.length === 0) {
      toast.error('Dodaj bar jednu sliku.');
      return;
    }

    setIsExtracting(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('images', f));
      if (instructions.trim().length > 0) {
        fd.append('instructions', instructions.trim());
      }

      const res = await fetch(`${API_BASE_URL}/api/ai-import/clients-from-images`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user?.token}`
        },
        body: fd
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || 'Greška pri AI ekstrakciji');
      }

      const normalized = Array.isArray(data?.clients) ? data.clients.map(normalizeExtractedClient) : [];

      setExtractedClients(normalized);
      const reviewCount = normalized.filter((c) => c.phoneNeedsReview).length;
      if (reviewCount > 0) {
        toast.warn(
          `Ekstrahovano klijenata: ${normalized.length}. ${reviewCount} zahteva potvrdu telefona.`,
          { autoClose: 6000 }
        );
      } else {
        toast.success(`Ekstrahovano klijenata: ${normalized.length}`);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Greška pri AI ekstrakciji');
    } finally {
      setIsExtracting(false);
    }
  };

  const saveSelectedClients = async () => {
    if (selectedCount === 0) {
      toast.error('Nema čekiranih klijenata za čuvanje.');
      return;
    }
    setIsSaving(true);
    setSaveClientErrors([]);
    try {
      const selected = extractedClients.filter((c) => c.selected);
      let ok = 0;
      let fail = 0;
      const errors = [];

      for (const c of selected) {
        try {
          const normalizedPhone = normalizePhoneForSave(c.phone);
          if (c.phoneNeedsReview) {
            throw new Error(
              `Telefon za "${c.name || 'klijent'}" nije automatski potvrđen — ispravite i označite red.`
            );
          }
          if (!isPhoneStructurallyValid(normalizedPhone)) {
            throw new Error(`Neispravan telefon "${c.phone || ''}". Proveri broj pre čuvanja.`);
          }
          if (isLikelyWrongAiPhone(normalizedPhone)) {
            throw new Error(
              `Sumnjiv telefon "${normalizedPhone}" (verovatno pogrešan prefiks iz AI). Ispravi broj pre čuvanja.`
            );
          }

          await createClient({
            name: c.name,
            phone: normalizedPhone,
            email: c.email,
            description: c.description,
            rating: c.rating,
            address: c.address
          });
          ok++;
        } catch (err) {
          fail++;
          const apiData = err?.response?.data || {};
          const rawMessage = apiData?.message || err?.message || 'Nepoznata greška';
          const isDuplicatePhone =
            rawMessage.toLowerCase().includes('brojem telefona već postoji') ||
            rawMessage.toLowerCase().includes('already exists');

          errors.push({
            name: c.name || '(bez imena)',
            phone: c.phone || '(bez telefona)',
            reason: isDuplicatePhone
              ? `Duplikat telefona${apiData?.existingClient?.name ? ` (već postoji: ${apiData.existingClient.name})` : ''}`
              : rawMessage
          });

          console.warn('Create client failed:', apiData || err?.message);
        }
      }

      if (ok > 0) toast.success(`Sačuvano klijenata: ${ok}`);
      if (fail > 0) toast.warn(`Nije sačuvano: ${fail} (proveri podatke / duplikate / limit paketa)`);
      setSaveClientErrors(errors);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">AI Import</h1>
              <p className="text-gray-300 mt-1">
                Dodaj slike, proveri, sačuvaj.
              </p>
              {!canUseAI && (
                <div className="mt-3 text-sm text-amber-300 bg-amber-900/20 border border-amber-700 rounded-lg p-3">
                  {restrictionMsg || 'AI Import nije dostupan u ovom paketu.'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 text-gray-100 hover:bg-gray-600 cursor-pointer">
                <Upload className="w-4 h-4" />
                <span>Dodaj slike/dokumente</span>
                <input
                  type="file"
                  accept="image/*,.pdf,.xlsx"
                  multiple
                  className="hidden"
                  onChange={(e) => onPickFiles(e.target.files)}
                />
              </label>
              <span className="text-sm text-gray-400">Max {MAX_FILES} slika</span>
            </div>

            <button
              onClick={extractFromImages}
              disabled={!canUseAI || isExtracting || files.length === 0}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                !canUseAI || isExtracting || files.length === 0
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
              }`}
              title={!canUseAI ? restrictionMsg || '' : ''}
              >
              {isExtracting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Ekstrakcija...
                </span>
              ) : (
                'Ekstrahuj klijente sa slika'
              )}
            </button>
            </div>

            <div className="w-full">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Instrukcije (opciono)
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-gray-100 text-sm min-h-[60px]"
                placeholder="Kratko uputstvo (opciono)..."
              />
            </div>
          </div>

          {previews.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-5">
              {previews.map((src, idx) => (
                <div key={idx} className="relative group">
                  {src ? (
                    <img
                      src={src}
                      alt={`upload-${idx}`}
                      className="w-full h-28 object-cover rounded-lg border border-gray-700"
                    />
                  ) : (
                    <div className="w-full h-28 object-cover rounded-lg border border-gray-700 bg-gray-900 flex items-center justify-center text-[10px] text-gray-300 px-2 text-center whitespace-pre-line">
                      {files[idx]?.name
                        ? `Dokument\n${files[idx].name.slice(0, 22)}...`
                        : 'Dokument'}
                    </div>
                  )}
                  <button
                    onClick={() => removeFileAt(idx)}
                    className="absolute top-2 right-2 p-2 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
                    title="Ukloni"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {extractedClients.length > 0 && (
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-bold text-white">Provera rezultata</h2>
                <p className="text-sm text-gray-400">
                  Čekiraj i sačuvaj.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => toggleAll(true)}
                  className="px-3 py-2 rounded bg-gray-700 text-gray-200 hover:bg-gray-600"
                >
                  <span className="inline-flex items-center gap-2">
                    <CheckSquare className="w-4 h-4" />
                    Sve
                  </span>
                </button>
                <button
                  onClick={() => toggleAll(false)}
                  className="px-3 py-2 rounded bg-gray-700 text-gray-200 hover:bg-gray-600"
                >
                  <span className="inline-flex items-center gap-2">
                    <Square className="w-4 h-4" />
                    Ništa
                  </span>
                </button>
                <button
                  onClick={saveSelectedClients}
                  disabled={isSaving || selectedCount === 0}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    isSaving || selectedCount === 0
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isSaving ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Čuvanje...
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Save className="w-4 h-4" />
                      Sačuvaj ({selectedCount})
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-700 text-gray-200">
                  <tr>
                    <th className="p-3 text-left">OK</th>
                    <th className="p-3 text-left">Ime</th>
                    <th className="p-3 text-left">Isečak</th>
                    <th className="p-3 text-left">OCR</th>
                    <th className="p-3 text-left">Telefon</th>
                    <th className="p-3 text-left">Mejl</th>
                    <th className="p-3 text-left">Ulica</th>
                    <th className="p-3 text-left">Broj</th>
                    <th className="p-3 text-left">Ocena</th>
                    <th className="p-3 text-left">Napomena</th>
                  </tr>
                </thead>
                <tbody>
                  {extractedClients.map((c, idx) => (
                    <tr
                      key={idx}
                      className={`border-b border-gray-700 ${c.phoneNeedsReview ? 'bg-amber-900/15' : ''}`}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={c.selected}
                          onChange={() => toggleClientSelected(idx)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          value={c.name}
                          onChange={(e) => updateClientField(idx, 'name', e.target.value)}
                          className="w-56 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="Ime i prezime"
                        />
                      </td>
                      <td className="p-3">
                        {c.phoneCropBox && previews[c.phoneCropBox?.imageIndex ?? 0] ? (
                          <PhoneCropPreview
                            imageSrc={previews[c.phoneCropBox?.imageIndex ?? 0]}
                            cropBox={c.phoneCropBox}
                          />
                        ) : (
                          <span className="text-xs text-gray-500">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="w-36 text-xs font-mono text-gray-300">
                          {c.phoneOcrReading || c.phoneFromVerifierAI || '—'}
                        </div>
                      </td>
                      <td className="p-3">
                        <input
                          value={c.phone}
                          onChange={(e) => {
                            updateClientField(idx, 'phone', e.target.value);
                          }}
                          className={`w-40 px-2 py-1 rounded bg-gray-900 border text-gray-100 ${
                            c.phoneNeedsReview ? 'border-amber-500' : 'border-gray-700'
                          }`}
                          placeholder="+381..."
                        />
                        {c.phoneNeedsReview && (
                          <span className="block text-[10px] text-amber-300 mt-0.5">potvrdi telefon</span>
                        )}
                      </td>
                      <td className="p-3">
                        <input
                          value={c.email}
                          onChange={(e) => updateClientField(idx, 'email', e.target.value)}
                          className="w-56 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="mejl"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          value={c.address.street}
                          onChange={(e) => updateClientAddressField(idx, 'street', e.target.value)}
                          className="w-56 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="Ulica"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          value={c.address.number}
                          onChange={(e) => updateClientAddressField(idx, 'number', e.target.value)}
                          className="w-24 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="Broj"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={c.rating ?? ''}
                          onChange={(e) =>
                            updateClientField(
                              idx,
                              'rating',
                              e.target.value === '' ? null : Number(e.target.value)
                            )
                          }
                          className="w-20 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="1-10"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          value={c.description}
                          onChange={(e) => updateClientField(idx, 'description', e.target.value)}
                          className="w-80 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="Napomena (opciono)"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {saveClientErrors.length > 0 && (
              <div className="mt-5 border border-red-700 bg-red-900/20 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-red-300 mb-2">
                  Nije sačuvano
                </h3>
                <div className="space-y-2 max-h-56 overflow-auto pr-1">
                  {saveClientErrors.map((e, i) => (
                    <div key={`${e.phone}-${i}`} className="text-sm text-red-200 border-b border-red-800/50 pb-2">
                      <span className="font-medium">{e.name}</span>
                      {' - '}
                      <span>{e.phone}</span>
                      {' -> '}
                      <span>{e.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Spare parts AI import (invoices -> parts) */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 text-gray-100 hover:bg-gray-600 cursor-pointer">
                <Upload className="w-4 h-4" />
                <span>Dodaj slike/dokumente</span>
                <input
                  type="file"
                  accept="image/*,.pdf,.xlsx"
                  multiple
                  className="hidden"
                  onChange={(e) => onPickSpareFiles(e.target.files)}
                />
              </label>
              <span className="text-sm text-gray-400">Max {MAX_FILES} slika</span>
            </div>

            <button
              onClick={extractSparePartsFromImages}
              disabled={!canUseAI || isExtractingSpare || spareFiles.length === 0}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                !canUseAI || isExtractingSpare || spareFiles.length === 0
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
              }`}
              title={!canUseAI ? restrictionMsg || '' : ''}
            >
              {isExtractingSpare ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Ekstrakcija...
                </span>
              ) : (
                'Ekstrahuj rezervne delove sa slika'
              )}
            </button>
          </div>

          {sparePreviews.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-5">
              {sparePreviews.map((src, idx) => (
                <div key={idx} className="relative group">
                  {src ? (
                    <img
                      src={src}
                      alt={`upload-spare-${idx}`}
                      className="w-full h-28 object-cover rounded-lg border border-gray-700"
                    />
                  ) : (
                    <div className="w-full h-28 object-cover rounded-lg border border-gray-700 bg-gray-900 flex items-center justify-center text-[10px] text-gray-300 px-2 text-center whitespace-pre-line">
                      {spareFiles[idx]?.name
                        ? `Dokument\n${spareFiles[idx].name.slice(0, 22)}...`
                        : 'Dokument'}
                    </div>
                  )}
                  <button
                    onClick={() => removeSpareFileAt(idx)}
                    className="absolute top-2 right-2 p-2 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
                    title="Ukloni"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {!canUseAI && (
            <div className="mt-4 text-sm text-amber-300 bg-amber-900/20 border border-amber-700 rounded-lg p-3">
              {restrictionMsg || 'AI Import nije dostupan u ovom paketu.'}
            </div>
          )}
        </div>

        {extractedSpareParts.length > 0 && (
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-bold text-white">Provera rezultata</h2>
                <p className="text-sm text-gray-400">
                  Čekiraj i sačuvaj.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => toggleSpareAll(true)}
                  className="px-3 py-2 rounded bg-gray-700 text-gray-200 hover:bg-gray-600"
                >
                  <span className="inline-flex items-center gap-2">
                    <CheckSquare className="w-4 h-4" />
                    Sve
                  </span>
                </button>
                <button
                  onClick={() => toggleSpareAll(false)}
                  className="px-3 py-2 rounded bg-gray-700 text-gray-200 hover:bg-gray-600"
                >
                  <span className="inline-flex items-center gap-2">
                    <Square className="w-4 h-4" />
                    Ništa
                  </span>
                </button>
                <button
                  onClick={saveSelectedSpareParts}
                  disabled={isSavingSpare || spareSelectedCount === 0}
                  className={`px-4 py-2 rounded-lg font-semibold transition ${
                    isSavingSpare || spareSelectedCount === 0
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isSavingSpare ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Čuvanje...
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Save className="w-4 h-4" />
                      Sačuvaj ({spareSelectedCount})
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-700 text-gray-200">
                  <tr>
                    <th className="p-3 text-left">OK</th>
                    <th className="p-3 text-left">Naziv</th>
                    <th className="p-3 text-left">Šifra</th>
                    <th className="p-3 text-left">Količina</th>
                    <th className="p-3 text-left">Cena</th>
                  </tr>
                </thead>
                <tbody>
                  {extractedSpareParts.map((p, idx) => (
                    <tr key={idx} className="border-b border-gray-700">
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={p.selected}
                          onChange={() => toggleSparePartSelected(idx)}
                        />
                      </td>
                      <td className="p-3">
                        <input
                          value={p.name}
                          onChange={(e) => updateSpareField(idx, 'name', e.target.value)}
                          className="w-56 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="Naziv rezervnog dela"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          value={p.code}
                          onChange={(e) => updateSpareField(idx, 'code', e.target.value)}
                          className="w-40 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="Šifra"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          type="number"
                          value={p.quantity}
                          onChange={(e) => updateSpareField(idx, 'quantity', e.target.value)}
                          className="w-24 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="npr 2"
                          min="0"
                        />
                      </td>
                      <td className="p-3">
                        <input
                          type="number"
                          value={p.unitPrice}
                          onChange={(e) => updateSpareField(idx, 'unitPrice', e.target.value)}
                          className="w-32 px-2 py-1 rounded bg-gray-900 border border-gray-700 text-gray-100"
                          placeholder="npr 1500"
                          min="0"
                          step="0.01"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIImportView;

